import React, { Fragment, useEffect, useState } from "react";
import Avatar from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Text } from "@/components/ui/text";
import { ArrowLeft2, Edit2, SearchNormal1 } from "iconsax-react";

import { getUserInitials } from "@/lib/utils";
import Multiline from "@/components/ui/multiline";
// import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs-variant1.";
import NoDataView from "@/components/ui/no-data-view";
import { Check, Pencil, X } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useGuestlist } from "@/hooks/use-event-group-participants";
import { Loader } from "@/components/ui/loader";
import useInView from "@/hooks/use-in-view";
import useDebounce from "@/hooks/use-debounce";
import SkeletonListLoader from "@/components/app/manage-event/guest-skelaton";
import { useQueryClient } from "@tanstack/react-query";
import { getQueryKey } from "@key.ai/constants";
import { Button } from "@/components/ui/button";

const GroupGuests = ({
  setOpened,
  selectedGroup,
  users,
  emails,
  handleToggleEmailSelection,
  handleSelectAll,
  toggleUserSelection,
  checkIsUserPresent,
  toggleSelectAll,
  checkIfAllSelect,
  onEditGroupClick
}: any) => {
  const [search, setSearch] = useState<string>("");
  const finalSearchValue = useDebounce(search, 300);

  const {
    data: groupParticipants,
    fetchNextPage: fetchNextGroupParticipants,
    hasNextPage: hasNextGroupParticipants,
    isLoading: loadingGroupParticipants,
    isFetchingNextPage: isFetchingNextGroupParticipants,
    totalRecords: totalGroupParticipantsRecords
  } = useGuestlist({
    groupId: selectedGroup?.id,
    search: finalSearchValue
  });

  const [allGroupsRef, allCalInView] = useInView({
    threshold: 0.1
  });

  const queryKey = getQueryKey("EVENT_GROUP_PARTICIPANTS");

  const queryClient = useQueryClient();

  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: [queryKey, selectedGroup?.id, search] });
    };
  }, [queryKey, selectedGroup?.id, search, queryClient]);

  useEffect(() => {
    if (allCalInView && !isFetchingNextGroupParticipants && !loadingGroupParticipants)
      fetchNextGroupParticipants();
  }, [
    allCalInView,
    fetchNextGroupParticipants,
    isFetchingNextGroupParticipants,
    loadingGroupParticipants
  ]);

  const filteredEmails = users.filter((email: any) =>
    email?.email.toLowerCase().includes(search.toLowerCase())
  );

  const isSelectAll = users.every((user: any) =>
    emails.some((email: any) => email.email === user.email && email.selected === true)
  );

  if (loadingGroupParticipants) {
    return <SkeletonListLoader />;
  }

  const isDataAvailable =
    groupParticipants &&
    groupParticipants?.pages[0] &&
    groupParticipants?.pages[0]?.data?.length > 0;

  return (
    <div>
      <div className="my-s">
        <Multiline
          className="bg-transparent px-0 !mx-0"
          icon={
            <div className="relative flex  items-center cursor-pointer">
              <ArrowLeft2
                className="mr-m cursor-pointer"
                onClick={() => {
                  setOpened(false);
                }}
              />
              <Avatar
                imgSrc={selectedGroup?.image}
                size={56}
                activeRing={false}
                variant="image"
                present={false}
                presence={false}
                presenceState="blocked"
              />
            </div>
          }
          titleText={
            <Text
              variant={"body_three_strong"}
              className="font-semibold flex gap-2 transition-all duration-300 ease-in-out mb-0.5"
            >
              {selectedGroup?.name}{" "}
              <span className=" cursor-pointer flex items-center">
                {" "}
                <Pencil
                  size={20}
                  color="#B2B2B2"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditGroupClick?.();
                  }}
                />{" "}
              </span>
            </Text>
          }
          text1={`Group | ${totalGroupParticipantsRecords} People`}
          titleTextSize={"medium"}
          text1Size={"body_one"}
          action={
            isDataAvailable && (
              <div className="flex items-center gap-s mr-xs">
                <Button
                  variant="text"
                  onClick={() => {
                    // handleSelectAll(e);
                    toggleSelectAll(
                      "Group",
                      selectedGroup?.id,
                      [],
                      checkIfAllSelect("Group", selectedGroup?.id) ? false : true,
                      selectedGroup?.numberOfGuests
                    );
                  }}
                  className="ml-1"
                >
                  {checkIfAllSelect("Group", selectedGroup?.id) ? "Deselect all" : "Select all"}
                </Button>
              </div>
            )
          }
        />
      </div>
      <div className="border-b border-gray-200 w-full"></div>

      {/* {isDataAvailable && ( */}
      <div className="px-l mt-2xl">
        <Input
          variant="medium-s"
          className="rounded-xs w-full px-xl bg-[#F5F6F9]"
          inputClassName="w-full"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          leftSectionBgClassName="bg-transparent !mr-2 items-center content-center p-none m-auto "
          leftSection={<SearchNormal1 size="24" variant="Outline" />}
          rightSectionClassName="py-none px-none pr-m-nudge"
          rightSection={
            search && (
              <X
                size={20}
                onClick={() => {
                  setSearch("");
                }}
              ></X>
            )
          }
        />
      </div>

      <ScrollArea
        className="h-[calc(100dvh-200px)]  flex flex-col mt-m"
        viewportClassName="h-[calc(100dvh-200px)]"
        type="always"
      >
        {isDataAvailable ? (
          groupParticipants?.pages?.map((page: any) =>
            page?.data?.map((user: any, index: number) => {
              const initals = getUserInitials(user);
              const isSelected = checkIsUserPresent("Group", selectedGroup?.id, user?.email);
              return (
                <>
                  <Fragment>
                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        toggleUserSelection(
                          "Group",
                          selectedGroup?.id,
                          user?.email,
                          isSelected ? false : true
                        );
                      }}
                      key={index}
                    >
                      <Multiline
                        key={index}
                        icon={
                          <div className="flex gap-m items-center">
                            <Avatar
                              text={initals}
                              imgSrc={user?.profile_photo}
                              size={56}
                              activeRing={true}
                              variant={user?.profile_photo ? "image" : "initials"}
                              present={false}
                              presence={false}
                            />
                          </div>
                        }
                        text1={user?.email}
                        action={isSelected && <Check />}
                        titleTextSize={"medium"}
                        text1Size={"small"}
                      />
                    </div>
                    <div className="border-b border-gray-200 w-full"></div>
                  </Fragment>
                </>
              );
            })
          )
        ) : (
          <div className="content-center h-full mx-auto text-center">
            <NoDataView title={`No members found.`} imageKey={"guest"} />
          </div>
        )}

        {isFetchingNextGroupParticipants && (
          <div className="mx-auto mt-3">
            <Loader />
          </div>
        )}

        <div ref={allGroupsRef} className="h-5 " />
      </ScrollArea>

      <div className="w-full  mt-s absolute bottom-0   bg-white">
        <div className="flex gap-m justify-end my p-4">
          <Button
            variant={"primary"}
            className="w-[150px]"
            onClick={() => {
              setOpened(false);
            }}
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GroupGuests;
